/**
 * 项目概览弹窗组件
 * 点击项目卡片后弹出，展示项目详细数据
 * 底部按钮固定显示，不随内容滚动
 */
import React, { useState, useEffect, useRef } from 'react';
import { Modal, Spin, message } from 'antd';
import type { ProjectOverviewData } from '../../services/dashboardService';
import { StatisticsService } from '../../services/statisticsService';
import { useResponsiveConfig } from '../../responsive/hooks/useResponsiveConfig';
import OverviewStats from './OverviewStats';
import CostBreakdown from './CostBreakdown';
import BudgetComparison from './BudgetComparison';
import ProjectInfo from './ProjectInfo';
import DetailRecords from './DetailRecords';
import ActionButtons from './ActionButtons';
import AddRecordModal, { RecordType } from './AddRecordModal';

export interface ProjectOverviewModalProps {
  visible: boolean;
  project: ProjectOverviewData | null;
  onClose: () => void;
  onExport?: (project: ProjectOverviewData) => void;
  onEditProject?: (project: ProjectOverviewData) => void;
}

// 缓存数据结构
interface CacheData {
  data: any;
  timestamp: number;
}

// 缓存有效期（1分钟）
const CACHE_TTL = 60 * 1000;

// 全局缓存
const statsCache = new Map<string, CacheData>();

// 清除项目缓存
export const clearProjectStatsCache = (projectId?: string) => {
  if (projectId) {
    statsCache.delete(projectId);
  } else {
    statsCache.clear();
  }
};

export const ProjectOverviewModal: React.FC<ProjectOverviewModalProps> = ({
  visible,
  project,
  onClose,
  onExport,
  onEditProject
}) => {
  const { isMobile, isTablet } = useResponsiveConfig();
  const [loading, setLoading] = useState(false);
  const [projectStats, setProjectStats] = useState<any>(null);
  const loadingRef = useRef(false);

  // 新增记录弹窗状态
  const [addRecordVisible, setAddRecordVisible] = useState(false);
  const [addRecordType, setAddRecordType] = useState<RecordType | null>(null);

  // 获取项目统计数据
  const fetchProjectStats = async (projectId: string, forceRefresh = false) => {
    // 检查缓存（非强制刷新时）
    if (!forceRefresh) {
      const cached = statsCache.get(projectId);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        setProjectStats(cached.data);
        return;
      }
    }

    // 防止重复请求
    if (loadingRef.current) return;
    loadingRef.current = true;

    setLoading(true);
    try {
      const response = await StatisticsService.getProjectComprehensiveStats(projectId);
      if (response.success && response.data) {
        setProjectStats(response.data);
        // 更新缓存
        statsCache.set(projectId, {
          data: response.data,
          timestamp: Date.now()
        });
      }
    } catch (error) {
      console.error('获取项目统计数据失败:', error);
      message.error('获取项目统计数据失败');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  // 当弹窗打开且有项目数据时，获取统计数据
  useEffect(() => {
    if (visible && project?.id) {
      fetchProjectStats(project.id);
    }
    if (!visible) {
      setProjectStats(null);
    }
  }, [visible, project?.id]);

  // 计算弹窗宽度
  const getModalWidth = () => {
    if (isMobile) return '100%';
    if (isTablet) return '90%';
    return 900;
  };

  // 打开新增记录弹窗
  const handleOpenAddRecord = (type: RecordType) => {
    setAddRecordType(type);
    setAddRecordVisible(true);
  };

  // 关闭新增记录弹窗
  const handleCloseAddRecord = () => {
    setAddRecordVisible(false);
    setAddRecordType(null);
  };

  // 新增记录成功后刷新数据
  const handleAddRecordSuccess = () => {
    if (project?.id) {
      // 清除缓存并重新获取数据
      clearProjectStatsCache(project.id);
      fetchProjectStats(project.id, true);
    }
  };

  if (!project) return null;

  // 渲染底部按钮（作为 footer）
  const renderFooter = () => (
    <ActionButtons
      project={project}
      onAddWorkTime={() => handleOpenAddRecord('worktime')}
      onAddEquipment={() => handleOpenAddRecord('equipment')}
      onAddMaterial={() => handleOpenAddRecord('material')}
      onExport={onExport}
      onEditProject={onEditProject}
      isMobile={isMobile}
    />
  );

  return (
    <>
      <Modal
        title={
          <span>
            📊 项目概览 - {project.name}
          </span>
        }
        open={visible}
        onCancel={onClose}
        footer={renderFooter()}
        width={getModalWidth()}
        style={isMobile ? { top: 0, margin: 0, maxWidth: '100%', paddingBottom: 0 } : {}}
        styles={{
          body: {
            maxHeight: isMobile ? 'calc(100vh - 220px)' : 'calc(70vh - 80px)',
            overflow: 'auto',
            padding: isMobile ? '12px' : '24px'
          },
          footer: {
            borderTop: '1px solid #f0f0f0',
            padding: isMobile ? '0 12px' : '0 24px',
            margin: 0
          }
        }}
        destroyOnClose
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Spin size="large" tip="加载项目数据中..." />
          </div>
        ) : (
          <div>
            {/* 统计卡片 */}
            <OverviewStats
              project={project}
              stats={projectStats}
              isMobile={isMobile}
            />

            {/* 成本构成 */}
            <CostBreakdown
              stats={projectStats}
              isMobile={isMobile}
            />

            {/* 预算对比 */}
            <BudgetComparison
              project={project}
              isMobile={isMobile}
            />

            {/* 项目信息 */}
            <ProjectInfo
              project={project}
              isMobile={isMobile}
            />

            {/* 详细记录（折叠面板） */}
            <DetailRecords
              projectId={project.id}
              stats={projectStats}
              isMobile={isMobile}
            />
          </div>
        )}
      </Modal>

      {/* 新增记录弹窗 */}
      <AddRecordModal
        visible={addRecordVisible}
        recordType={addRecordType}
        projectId={project.id}
        projectName={project.name}
        onClose={handleCloseAddRecord}
        onSuccess={handleAddRecordSuccess}
        isMobile={isMobile}
      />
    </>
  );
};

export default ProjectOverviewModal;
